import {useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import {useSelector} from "react-redux";

import VLFModal from "@/components/Modal";
import VLFSelectNew from "@/components/Input/SelectNew";
import VLFDatePicker from "@/components/Input/DatePicker";
import VLFTextField from "@/components/Input/TextField";

import {PlayerAPI} from "@/api/player";
import {RosterAPI} from "@/api/roster";
import {ContractAPI} from "@/api/contract";

import {
	REQUIRED_FIELD,
	ERROR_OPERATION,
	SUCCESSFUL_OPERATION,
	getInternalClubId,
	statusOptions,
} from "@/utils/constants";
import {checkRequiredFields, getRosterName, visibleRosters} from "@/utils/functions";

import "./index.scss";

const DESTINATION_OPTIONS = [
	{value: "roster", label: "Assegna a una rosa"},
	{value: "pool", label: "Aggiungi al pool"},
];

const contractTypeOptions = [
	{value: "professional", label: "Professionistico"},
	{value: "amateur", label: "Dilettantistico"},
	{value: "youth", label: "Settore giovanile"},
];

function EnrollPlayerModal({open, close, playerId, playerData, onSuccess, handleNotification}) {
	const navigate = useNavigate();
	const seasonId = useSelector((state) => state?.season?.id);

	const [rosters, setRosters] = useState([]);
	const [loading, setLoading] = useState(false);
	const [errors, setErrors] = useState([]);
	const [fields, setFields] = useState({
		destination: DESTINATION_OPTIONS[0],
		roster: null,
		status: {value: "Interno", label: "Interno"},
		jerseyNumber: "",
		contractType: null,
		contractStart: null,
		contractEnd: null,
	});

	useEffect(() => {
		if (!open || !seasonId) return;
		RosterAPI.list({seasonId, prospect: false, clubId: getInternalClubId()})
			.then((res) => {
				setRosters(
					visibleRosters(
						(res || []).filter(
							(r) => !r.prospect && (!r.clubId || r.clubId === getInternalClubId()),
						),
					).map((r) => ({value: r.id, label: getRosterName(r)})),
				);
			})
			.catch(() => {});
	}, [open, seasonId]);

	useEffect(() => {
		if (!open) {
			setFields({
				destination: DESTINATION_OPTIONS[0],
				roster: null,
				status: {value: "Interno", label: "Interno"},
				jerseyNumber: "",
				contractType: null,
				contractStart: null,
				contractEnd: null,
			});
			setErrors([]);
		}
	}, [open]);

	const handleConfirm = async () => {
		const toPool = fields.destination?.value === "pool";
		const errs = checkRequiredFields(toPool ? [] : ["roster"], fields);
		setErrors(errs);
		if (errs.length) return;

		setLoading(true);
		try {
			await PlayerAPI.update(playerId, {
				...playerData,
				status: fields.status?.value || "Interno",
				...(toPool ? {addToPool: true} : {}),
			});

			if (!toPool) {
				await RosterAPI.addPlayers([{
					rosterId: fields.roster.value,
					playerId,
				}]);
			}

			if (fields.contractType && fields.contractStart && fields.contractEnd) {
				await ContractAPI.create({
					playerId,
					contractType: fields.contractType.value,
					startDate: fields.contractStart,
					endDate: fields.contractEnd,
				});
			}

			handleNotification(SUCCESSFUL_OPERATION, "success");
			if (onSuccess) onSuccess();
			close();
			navigate("/players/" + playerId);
		} catch {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	return (
		<VLFModal
			maxWidth="md"
			open={open}
			title="Tesseramento giocatore"
			close={close}
			confirm={handleConfirm}
			confirmLabel="Tessera"
			loading={loading}
			content={
				<div className="enroll-modal">
					{/* Player info */}
					{playerData && (
						<div className="enroll-modal__player">
							<span className="enroll-modal__player-name">
								{playerData.firstName} {playerData.lastName}
							</span>
							{playerData.dateOfBirth && (
								<span className="enroll-modal__player-meta">
									{new Date(playerData.dateOfBirth).toLocaleDateString("it-IT")}
								</span>
							)}
							{playerData.primaryRole && (
								<span className="enroll-modal__player-meta">
									{playerData.primaryRole}
								</span>
							)}
						</div>
					)}

					<div className="row">
						{/* Destinazione */}
						<div className="col-sm-12 col-md-6">
							<VLFSelectNew
								options={DESTINATION_OPTIONS}
								value={fields.destination}
								onChange={(val) => {
									setErrors([]);
									setFields({
										...fields,
										destination: val,
										roster: val?.value === "roster" ? fields.roster : null,
									});
								}}
								label="Destinazione"
								sorted={false}
							/>
						</div>

						{/* Rosa (obbligatorio se destinazione = rosa) */}
						{fields.destination?.value === "roster" && (
							<div className="col-sm-12 col-md-6">
								<VLFSelectNew
									options={rosters}
									value={fields.roster}
									onChange={(val) => {
										setErrors([]);
										setFields({...fields, roster: val});
									}}
									label="Rosa di destinazione"
									required
									sorted={false}
									helperText={errors.indexOf("roster") !== -1 ? REQUIRED_FIELD : ""}
									error={errors.indexOf("roster") !== -1}
								/>
							</div>
						)}

						{/* Status */}
						<div className="col-sm-12 col-md-6">
							<VLFSelectNew
								options={statusOptions.filter((s) => s.value !== "Esterno")}
								value={fields.status}
								onChange={(val) => setFields({...fields, status: val})}
								label="Status"
								sorted={false}
							/>
						</div>

						{/* Numero maglia */}
						{fields.destination?.value === "roster" && (
						<div className="col-sm-12 col-md-6">
							<VLFTextField
								name="jerseyNumber"
								value={fields.jerseyNumber}
								onChange={(e) => setFields({...fields, jerseyNumber: e.target.value})}
								label="Numero maglia"
								size="small"
								type="number"
							/>
						</div>
						)}
					</div>

					{/* Contratto (opzionale) */}
					<div className="enroll-modal__section-title">
						Contratto (opzionale)
					</div>
					<div className="row">
						<div className="col-sm-12 col-md-4">
							<VLFSelectNew
								options={contractTypeOptions}
								value={fields.contractType}
								onChange={(val) => setFields({...fields, contractType: val})}
								label="Tipo contratto"
								isClearable
								sorted={false}
							/>
						</div>
						<div className="col-sm-12 col-md-4">
							<VLFDatePicker
								label="Data inizio"
								value={fields.contractStart}
								onChange={(val) => setFields({...fields, contractStart: val})}
								size="small"
							/>
						</div>
						<div className="col-sm-12 col-md-4">
							<VLFDatePicker
								label="Data scadenza"
								value={fields.contractEnd}
								onChange={(val) => setFields({...fields, contractEnd: val})}
								size="small"
							/>
						</div>
					</div>
				</div>
			}
		/>
	);
}

export default EnrollPlayerModal;
